import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Group,
  Modal,
  NativeSelect,
  NumberInput,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { api, ApiError } from '@/lib/api'
import type {
  ProjectAISettingsResponse,
  ProjectAIUpdatePayload,
  ProjectDetailResponse,
  VLLMSyncResponse,
} from '@/types/api'
import { flattenOpenAIModelOptions } from './openai-models'

const DEFAULT_OPERATOR_HANDOFF_HINT_MESSAGE =
  'Если бот не смог помочь с вопросом и вам нужен живой специалист — напишите «передай оператору», и я передам обращение оператору.'

const DEFAULT_AI_POLICY = {
  profile: 'balanced',
  always_transfer_on_human_request: true,
  require_user_confirmation_for_resolve: true,
  forbid_closed_status_by_ai: true,
  block_unsafe_operation_promises: true,
  require_respond_or_transfer: true,
  allowed_actions: ['respond', 'add_tag', 'set_priority', 'set_status', 'transfer'],
}
const POLICY_PRESETS: Record<string, Record<string, unknown>> = {
  balanced: DEFAULT_AI_POLICY,
  strict_handoff: {
    ...DEFAULT_AI_POLICY,
    profile: 'strict_handoff',
    require_user_confirmation_for_resolve: true,
  },
  autonomous_max: {
    ...DEFAULT_AI_POLICY,
    profile: 'autonomous_max',
    require_user_confirmation_for_resolve: false,
  },
}
const POLICY_PROFILE_HINTS: Record<string, string> = {
  balanced:
    'Сбалансированный режим: бот помогает по базе, собирает данные и передает оператору при необходимости.',
  strict_handoff:
    'Более строгий режим: чаще эскалация оператору и консервативные решения в спорных сценариях.',
  autonomous_max:
    'Более автономный режим: бот старается закрывать больше типовых обращений, но без опасных действий.',
}

type Props = {
  projectId: string
  project: ProjectDetailResponse | null
  settings: ProjectAISettingsResponse | null
  canEdit: boolean
  onSettingsUpdated: () => void
}

export function AiSettingsTab({ projectId, project, settings, canEdit, onSettingsUpdated }: Props) {
  const [saving, setSaving] = useState(false)
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false)
  const [syncing, setSyncing] = useState<string | null>(null)
  const [applyingPolicy, setApplyingPolicy] = useState(false)

  const [isEnabled, setIsEnabled] = useState(false)
  const [emailAi, setEmailAi] = useState(false)
  const [provider, setProvider] = useState('openai')
  const [openaiKey, setOpenaiKey] = useState('')
  const [model, setModel] = useState('gpt-5.2')
  const [localUrl, setLocalUrl] = useState('')
  const [localModel, setLocalModel] = useState('')
  const [localKey, setLocalKey] = useState('')
  const [qdrantCollection, setQdrantCollection] = useState('')
  const [temperature, setTemperature] = useState('0.7')
  const [maxTokens, setMaxTokens] = useState<number | string>(1000)
  const [autoRespond, setAutoRespond] = useState(true)
  const [monitorTickets, setMonitorTickets] = useState(true)
  const [autoResolveHours, setAutoResolveHours] = useState<number | string>(12)
  const [silenceOperatorHintHours, setSilenceOperatorHintHours] = useState<number | string>(0)
  const [silenceOperatorHintMessage, setSilenceOperatorHintMessage] = useState('')
  const [requestReviewAfterAi, setRequestReviewAfterAi] = useState(false)
  const [fallbackProvider, setFallbackProvider] = useState('')
  const [fallbackOpenaiKey, setFallbackOpenaiKey] = useState('')
  const [fallbackAsstId, setFallbackAsstId] = useState('')
  const [fallbackVllmUrl, setFallbackVllmUrl] = useState('')
  const [fallbackVllmKey, setFallbackVllmKey] = useState('')
  const [fallbackVllmAsst, setFallbackVllmAsst] = useState('')
  const [policyProfile, setPolicyProfile] = useState('balanced')
  const [vllmModelOptions, setVllmModelOptions] = useState<{ value: string; label: string }[]>([])
  const [loadingVllmModels, setLoadingVllmModels] = useState(false)

  const enableReviewRequest = !!project?.enable_review_request

  const hydrate = useCallback(() => {
    if (!settings) return
    setIsEnabled(!!settings.is_enabled)
    setProvider(settings.provider_type || 'openai')
    setOpenaiKey(settings.openai_api_key || '')
    setModel(settings.model || 'gpt-5.2')
    setLocalUrl(settings.local_ai_url || '')
    setLocalModel(settings.local_ai_model || '')
    setLocalKey(settings.local_ai_api_key || '')
    setQdrantCollection(settings.vllm_qdrant_collection || '')
    setTemperature(settings.temperature ?? '0.7')
    setMaxTokens(settings.max_tokens ?? 1000)
    setAutoRespond(settings.auto_respond_enabled !== false)
    setMonitorTickets(settings.monitor_tickets !== false)
    setAutoResolveHours(settings.auto_resolve_hours ?? 12)
    setSilenceOperatorHintHours(settings.silence_operator_hint_hours ?? 0)
    setSilenceOperatorHintMessage(settings.silence_operator_hint_message ?? '')
    setRequestReviewAfterAi(!!settings.request_review_after_ai_resolve)
    setFallbackProvider(settings.fallback_provider_type || '')
    setFallbackOpenaiKey(settings.fallback_openai_api_key || '')
    setFallbackAsstId(settings.fallback_openai_assistant_id || '')
    setFallbackVllmUrl(settings.fallback_local_ai_url || '')
    setFallbackVllmKey(settings.fallback_local_ai_api_key || '')
    setFallbackVllmAsst(settings.fallback_vllm_assistant_id || '')
    const rawPolicy = settings.ai_policy && typeof settings.ai_policy === 'object' ? settings.ai_policy : DEFAULT_AI_POLICY
    const profile =
      typeof rawPolicy.profile === 'string' && rawPolicy.profile in POLICY_PRESETS
        ? rawPolicy.profile
        : 'balanced'
    setPolicyProfile(profile)
  }, [settings])

  useEffect(() => {
    hydrate()
  }, [hydrate])

  useEffect(() => {
    setEmailAi(!!project?.enable_ai_for_email_channel)
  }, [project?.enable_ai_for_email_channel])

  const silenceHintHoursNum =
    typeof silenceOperatorHintHours === 'number'
      ? silenceOperatorHintHours
      : parseInt(String(silenceOperatorHintHours), 10) || 0
  const projectScopeLabel = project?.name?.trim()
    ? `проекта «${project.name.trim()}»`
    : 'этого проекта'

  const activeInstructionCount =
    settings?.instructions?.filter((i) => i.is_active !== false).length ?? 0
  const activeKbCount =
    settings?.knowledge_base_items?.filter((k) => k.is_active !== false).length ?? 0
  const appliedPolicyProfile =
    settings?.ai_policy && typeof settings.ai_policy === 'object' && typeof (settings.ai_policy as Record<string, unknown>).profile === 'string'
      ? String((settings.ai_policy as Record<string, unknown>).profile)
      : 'balanced'
  const policyDirty = policyProfile !== appliedPolicyProfile
  const vllmCredentialsOk = Boolean(localUrl.trim() && localKey.trim())
  const vllmModelOk = Boolean(localModel.trim())
  const vllmPipelineReady =
    provider === 'vllm' &&
    isEnabled &&
    vllmCredentialsOk &&
    vllmModelOk &&
    Boolean(settings?.vllm_vector_store_id) &&
    Boolean(settings?.vllm_assistant_id)

  const assistantReady = settings?.assistant_ready === true
  const assistantReadyReason = settings?.assistant_ready_reason || ''

  const save = async (transferDecision?: 'release' | 'keep') => {
    if (!canEdit) return
    if (settings?.is_enabled && !isEnabled && !transferDecision) {
      setDisableConfirmOpen(true)
      return
    }
    if (provider === 'vllm' && isEnabled) {
      if (!localUrl.trim() || !localKey.trim()) {
        showNotification({
          title: 'Не хватает данных vLLM',
          message: 'Укажите URL шлюза и API-ключ проекта.',
          color: 'orange',
        })
        return
      }
      if (!localModel.trim()) {
        showNotification({
          title: 'Не указана модель',
          message: 'Загрузите список с шлюза или введите имя модели маршрута вручную.',
          color: 'orange',
        })
        return
      }
    }
    const mt = typeof maxTokens === 'number' ? maxTokens : parseInt(String(maxTokens), 10)
    const ar = typeof autoResolveHours === 'number' ? autoResolveHours : parseInt(String(autoResolveHours), 10)
    const hintH =
      typeof silenceOperatorHintHours === 'number'
        ? silenceOperatorHintHours
        : parseInt(String(silenceOperatorHintHours), 10)
    const payload: ProjectAIUpdatePayload = {
      is_enabled: isEnabled,
      provider_type: provider,
      openai_api_key: openaiKey.trim() || null,
      model: model.trim() || null,
      local_ai_url: localUrl.trim() || null,
      local_ai_model: provider === 'openai' ? null : localModel.trim() || null,
      local_ai_api_key: localKey.trim() || null,
      vllm_qdrant_collection: qdrantCollection.trim() || null,
      temperature: temperature.trim() || '0.7',
      max_tokens: Number.isFinite(mt) ? mt : 1000,
      auto_respond_enabled: autoRespond,
      monitor_tickets: monitorTickets,
      auto_resolve_hours: Number.isFinite(ar) ? ar : 12,
      silence_operator_hint_hours: Number.isFinite(hintH) ? hintH : 0,
      silence_operator_hint_message: silenceOperatorHintMessage.trim() || null,
      request_review_after_ai_resolve: requestReviewAfterAi,
      fallback_provider_type: fallbackProvider.trim() || null,
      fallback_openai_api_key: fallbackOpenaiKey.trim() || null,
      fallback_openai_assistant_id: fallbackAsstId.trim() || null,
      fallback_local_ai_url: fallbackVllmUrl.trim() || null,
      fallback_local_ai_api_key: fallbackVllmKey.trim() || null,
      fallback_vllm_assistant_id: fallbackVllmAsst.trim() || null,
      ai_policy: {
        ...(settings?.ai_policy && typeof settings.ai_policy === 'object' ? settings.ai_policy : {}),
        ...(POLICY_PRESETS[policyProfile] || POLICY_PRESETS.balanced),
        profile: policyProfile,
      },
    }
    setSaving(true)
    try {
      await api.projectAIUpdate(projectId, payload)
      await api.projectUpdate(projectId, { enable_ai_for_email_channel: emailAi })
      let message = ''
      if (transferDecision === 'release') {
        const result = await api.projectAIReleaseTickets(projectId)
        message = result.updated
          ? `В неназначенные переведено обращений: ${result.updated}.`
          : 'Активных обращений на Мозгоботе не найдено.'
      }
      setDisableConfirmOpen(false)
      showNotification({ title: 'Сохранено', message, color: 'green' })
      onSettingsUpdated()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  const loadVllmModels = async () => {
    if (!localUrl.trim() || !localKey.trim()) {
      showNotification({
        title: 'Нужны URL и API ключ',
        message: 'Укажите URL и API-ключ, нажмите «Сохранить настройки», затем загрузите список моделей.',
        color: 'orange',
      })
      return
    }
    setLoadingVllmModels(true)
    try {
      const res = await api.vllmResources(projectId)
      const models = res.models ?? []
      if (!models.length) {
        showNotification({
          title: 'Модели не найдены',
          message:
            'В балансере создайте маршрут модели (раздел «Маршруты») или дождитесь загрузки модели в «Хранилище».',
          color: 'orange',
        })
        setVllmModelOptions([])
        return
      }
      const opts = models.map((m) => {
        const slug = m.route_slug ? ` (${m.route_slug})` : ''
        const workers = m.workers != null ? ` · GPU: ${m.workers}` : ''
        return { value: m.id, label: `${m.id}${slug}${workers}` }
      })
      setVllmModelOptions(opts)
      if (!localModel.trim() && opts.length === 1) {
        setLocalModel(opts[0].value)
      }
      showNotification({ title: 'Список моделей обновлён', message: `${models.length} шт.`, color: 'green' })
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось получить модели с шлюза',
        color: 'red',
      })
    } finally {
      setLoadingVllmModels(false)
    }
  }

  const applyPolicyProfileNow = async () => {
    if (!canEdit) return
    setApplyingPolicy(true)
    try {
      await api.projectAIUpdate(projectId, {
        ai_policy: {
          ...(settings?.ai_policy && typeof settings.ai_policy === 'object' ? settings.ai_policy : {}),
          ...(POLICY_PRESETS[policyProfile] || POLICY_PRESETS.balanced),
          profile: policyProfile,
        },
      })
      showNotification({ title: 'Режим применён', message: '', color: 'green' })
      onSettingsUpdated()
    } catch (e) {
      showNotification({
        title: 'Ошибка применения режима',
        message: e instanceof ApiError ? e.message : 'Не удалось применить режим',
        color: 'red',
      })
    } finally {
      setApplyingPolicy(false)
    }
  }

  const runSync = async (key: string, fn: () => Promise<VLLMSyncResponse | unknown>) => {
    if (provider === 'vllm' && !vllmCredentialsOk) {
      showNotification({
        title: 'Сначала сохраните подключение',
        message: 'Укажите URL и API-ключ vLLM и нажмите «Сохранить настройки».',
        color: 'orange',
      })
      return
    }
    setSyncing(key)
    try {
      const res = await fn()
      const msg =
        res && typeof res === 'object' && 'message' in res && typeof (res as VLLMSyncResponse).message === 'string'
          ? (res as VLLMSyncResponse).message
          : ''
      showNotification({ title: 'Готово', message: msg || '', color: 'green' })
      onSettingsUpdated()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Операция не удалась',
        color: 'red',
      })
    } finally {
      setSyncing(null)
    }
  }

  const regenerateAssistantAfterUpdates = async () => {
    const key = provider === 'vllm' ? 'regen_vllm' : 'regen_openai'
    if (provider === 'vllm' && !vllmCredentialsOk) {
      showNotification({
        title: 'Сначала сохраните подключение',
        message: 'Укажите URL и API-ключ vLLM и нажмите «Сохранить настройки».',
        color: 'orange',
      })
      return
    }
    setSyncing(key)
    try {
      if (provider === 'vllm') {
        await api.syncVLLMVectorStore(projectId)
        await api.syncVLLMAssistant(projectId)
      } else {
        await api.syncVectorStore(projectId)
        await api.syncAssistant(projectId)
      }
      showNotification({
        title: 'Перегенерация завершена',
        message: 'База знаний и ассистент пересинхронизированы с новыми данными.',
        color: 'green',
      })
      onSettingsUpdated()
    } catch (e) {
      showNotification({
        title: 'Ошибка перегенерации',
        message: e instanceof ApiError ? e.message : 'Не удалось пересинхронизировать ассистента',
        color: 'red',
      })
    } finally {
      setSyncing(null)
    }
  }

  const modelOptions = flattenOpenAIModelOptions()
  const modelSelectData = modelOptions.some((o) => o.value === model)
    ? modelOptions
    : [{ value: model, label: `${model} (текущая)` }, ...modelOptions]

  if (!settings) return null

  return (
    <Stack gap="md">
      {!canEdit ? (
        <Alert color="gray" variant="light">
          Нет права «Управление проектами» — изменение недоступно.
        </Alert>
      ) : null}

      <Card withBorder padding="md" radius="md">
        <Text fw={600} mb="sm">
          Общие
        </Text>
        <Stack gap="sm">
          <Checkbox
            label="Включить AI ассистента"
            checked={isEnabled}
            onChange={(e) => setIsEnabled(e.currentTarget.checked)}
            disabled={!canEdit}
          />
          <Checkbox
            label="Мозгобот отвечает в канале Email"
            checked={emailAi}
            onChange={(e) => setEmailAi(e.currentTarget.checked)}
            disabled={!canEdit}
          />
          <Text size="xs" c="dimmed">
            Если выключено, обращения из почты не назначаются на Мозгобота.
          </Text>
        </Stack>
      </Card>

      {isEnabled ? (
        <Alert color={assistantReady ? 'green' : 'orange'} variant="light">
          {assistantReady
            ? 'Ассистент проекта готов к обработке обращений.'
            : `Ассистент проекта не готов: ${assistantReadyReason || 'проверьте ключи/assistant id и синхронизацию'}`}
        </Alert>
      ) : null}

      <Card withBorder padding="md" radius="md">
        <Text fw={600} mb="sm">
          AI Policy (по проекту)
        </Text>
        <Text size="xs" c="dimmed" mb="sm">
          Детерминированные правила качества для этого проекта и его ассистента. Применяются до исполнения действий LLM.
        </Text>
        <Alert color={policyDirty ? 'yellow' : 'green'} variant="light" mb="sm">
          Текущий применённый режим: <b>{appliedPolicyProfile}</b>.
          {policyDirty
            ? ` Есть несохранённые изменения политики. Нажмите «Использовать режим», чтобы применить их.`
            : ' Изменений режима нет, используется этот режим.'}
        </Alert>
        <Group mb="sm" grow>
          <NativeSelect
            label="Профиль"
            data={[
              { value: 'balanced', label: 'Balanced (рекомендуется)' },
              { value: 'strict_handoff', label: 'Strict handoff' },
              { value: 'autonomous_max', label: 'Autonomous max' },
            ]}
            value={policyProfile}
            onChange={(e) => setPolicyProfile(e.currentTarget.value)}
            disabled={!canEdit}
          />
          <Button
            mt={24}
            variant="light"
            disabled={!canEdit || !policyDirty}
            loading={applyingPolicy}
            onClick={() => void applyPolicyProfileNow()}
          >
            Использовать режим
          </Button>
        </Group>
        <Text size="xs" c="dimmed" mb="sm">
          {POLICY_PROFILE_HINTS[policyProfile] || POLICY_PROFILE_HINTS.balanced}
        </Text>
        <Group mb="sm">
          <Button
            variant="subtle"
            color="gray"
            disabled={!canEdit}
            onClick={() => {
              setPolicyProfile('balanced')
            }}
          >
            Сброс к рекомендуемому
          </Button>
        </Group>
        <Alert color="blue" variant="light">
          JSON-редактирование отключено: используется управляемый профиль политики, чтобы избежать ошибок и расхождений между проектами.
        </Alert>
      </Card>

      <Card withBorder padding="md" radius="md">
        <Text fw={600} mb="sm">
          Провайдер
        </Text>
        <NativeSelect
          label="Тип провайдера"
          data={[
            { value: 'openai', label: 'OpenAI' },
            { value: 'ollama', label: 'Ollama (локальный)' },
            { value: 'vllm', label: 'vLLM (локальный)' },
          ]}
          value={provider}
          onChange={(e) => setProvider(e.currentTarget.value)}
          disabled={!canEdit}
        />

        {provider === 'openai' ? (
          <Stack gap="sm" mt="md">
            <TextInput
              label="OpenAI API ключ"
              type="password"
              value={openaiKey}
              onChange={(e) => setOpenaiKey(e.target.value)}
              disabled={!canEdit}
            />
            <NativeSelect
              label="Модель"
              data={modelSelectData}
              value={model}
              onChange={(e) => setModel(e.currentTarget.value)}
              disabled={!canEdit}
            />
          </Stack>
        ) : null}

        {provider === 'ollama' || provider === 'vllm' ? (
          <Stack gap="sm" mt="md">
            <TextInput
              label="URL сервера"
              placeholder={provider === 'vllm' ? 'https://...' : 'http://localhost:11434'}
              value={localUrl}
              onChange={(e) => setLocalUrl(e.target.value)}
              disabled={!canEdit}
            />
            {provider === 'ollama' ? (
              <TextInput
                label="Название модели"
                placeholder="llama3, mistral, qwen…"
                value={localModel}
                onChange={(e) => setLocalModel(e.target.value)}
                disabled={!canEdit}
              />
            ) : null}
            {provider === 'vllm' ? (
              <>
                <TextInput
                  label="Имя модели (поле model в API)"
                  description="Скопируйте из GPU Balancer: «Хранилище моделей» (колонка после загрузки) или «Маршруты» → vllm_model. Должно совпадать с маршрутом на GPU."
                  placeholder="/data/models/Qwen2.5-14B-Instruct или slug маршрута"
                  value={localModel}
                  onChange={(e) => setLocalModel(e.target.value)}
                  disabled={!canEdit}
                />
                {vllmModelOptions.length > 0 ? (
                  <NativeSelect
                    label="Выбрать из шлюза"
                    data={vllmModelOptions}
                    value={vllmModelOptions.some((o) => o.value === localModel) ? localModel : ''}
                    onChange={(e) => {
                      if (e.currentTarget.value) setLocalModel(e.currentTarget.value)
                    }}
                    disabled={!canEdit}
                  />
                ) : null}
                <Button
                  size="xs"
                  variant="light"
                  loading={loadingVllmModels}
                  disabled={!canEdit || !vllmCredentialsOk}
                  onClick={() => void loadVllmModels()}
                >
                  Загрузить модели с шлюза (после сохранения)
                </Button>
              </>
            ) : null}
            <TextInput
              label={provider === 'vllm' ? 'API ключ (обязателен для vLLM)' : 'API ключ (если нужен)'}
              type="password"
              value={localKey}
              onChange={(e) => setLocalKey(e.target.value)}
              disabled={!canEdit}
            />
            {provider === 'vllm' ? (
              <TextInput
                label="Коллекция Qdrant (RAG)"
                value={qdrantCollection}
                onChange={(e) => setQdrantCollection(e.target.value)}
                disabled={!canEdit}
              />
            ) : null}
          </Stack>
        ) : null}

        <Group grow mt="md">
          <TextInput
            label="Температура"
            value={temperature}
            onChange={(e) => setTemperature(e.target.value)}
            disabled={!canEdit}
          />
          <NumberInput
            label="Макс. токенов ответа"
            description={provider === 'vllm' ? 'Для vLLM обычно 512–1024 (JSON-ответ + текст клиенту)' : undefined}
            value={maxTokens}
            onChange={setMaxTokens}
            min={100}
            max={32000}
            disabled={!canEdit}
          />
        </Group>
      </Card>

      <Card withBorder padding="md" radius="md">
        <Text fw={600} mb="sm">
          Отслеживание обращений
        </Text>
        <Stack gap="sm">
          <Checkbox label="Автоответ на новые сообщения" checked={autoRespond} onChange={(e) => setAutoRespond(e.currentTarget.checked)} disabled={!canEdit} />
          <Checkbox label="Отслеживать обращения до передачи оператору" checked={monitorTickets} onChange={(e) => setMonitorTickets(e.currentTarget.checked)} disabled={!canEdit} />
          <NumberInput
            label="Авто «Решено», часов (0 = выкл.)"
            description="Если обращение у Мозгобота в статусе «Открыто» или «В работе», последнее сообщение — от бота, и клиент не ответил за N часов — статус станет «Решено». Закрытие — в настройках проекта («Автозакрытие решённых»)."
            value={autoResolveHours}
            onChange={setAutoResolveHours}
            min={0}
            max={168}
            disabled={!canEdit}
          />
          <Stack gap="xs" pl="sm" style={{ borderLeft: '3px solid var(--mantine-color-gray-3)' }}>
            <Text size="sm" fw={600}>
              Напоминание про оператора ({projectScopeLabel})
            </Text>
            <Text size="xs" c="dimmed">
              У каждого проекта свои часы и свой текст. В других проектах настройте отдельно во вкладке «Мозгобот».
            </Text>
            <NumberInput
              label="Часов молчания клиента (0 = выкл.)"
              description="Если клиент не отвечает столько часов после последнего сообщения Мозгобота, бот один раз напишет, как передать обращение оператору. Повторно в том же обращении не отправляется."
              value={silenceOperatorHintHours}
              onChange={setSilenceOperatorHintHours}
              min={0}
              max={168}
              disabled={!canEdit}
            />
            {silenceHintHoursNum > 0 ? (
              <>
                <Textarea
                  label="Текст напоминания (необязательно)"
                  description="Пустое поле — стандартный текст для этого проекта. До 2000 символов."
                  placeholder={DEFAULT_OPERATOR_HANDOFF_HINT_MESSAGE}
                  value={silenceOperatorHintMessage}
                  onChange={(e) => setSilenceOperatorHintMessage(e.currentTarget.value)}
                  minRows={3}
                  maxLength={2000}
                  disabled={!canEdit}
                />
                {!silenceOperatorHintMessage.trim() ? (
                  <Text size="xs" c="dimmed">
                    Будет отправлено: {DEFAULT_OPERATOR_HANDOFF_HINT_MESSAGE}
                  </Text>
                ) : null}
              </>
            ) : (
              <Text size="xs" c="dimmed">
                Укажите число часов больше 0, чтобы включить напоминание и при необходимости задать свой текст.
              </Text>
            )}
          </Stack>
          <Checkbox
            label="Запрашивать отзыв после решения AI"
            checked={requestReviewAfterAi}
            onChange={(e) => setRequestReviewAfterAi(e.currentTarget.checked)}
            disabled={!canEdit || !enableReviewRequest}
          />
          {!enableReviewRequest ? (
            <Text size="xs" c="dimmed">
              Включите «Запрос отзыва» в настройках проекта (вкладка «Общее»), чтобы опция работала.
            </Text>
          ) : null}
        </Stack>
      </Card>

      {provider === 'openai' ? (
        <Card withBorder padding="md" radius="md">
          <Text fw={600} mb="sm">
            OpenAI: база знаний и ассистент
          </Text>
          <Alert color="blue" variant="light" mb="sm">
            Перед созданием новой базы удалите текущую. Затем создайте базу, затем при необходимости ассистента.
          </Alert>
          <Stack gap={4}>
            <Text size="sm">
              <strong>Vector Store:</strong>{' '}
              <Text span ff="monospace" size="sm">
                {settings.openai_vector_store_id || '—'}
              </Text>
            </Text>
            <Text size="sm">
              <strong>Assistant:</strong>{' '}
              <Text span ff="monospace" size="sm">
                {settings.openai_assistant_id || '—'}
              </Text>
            </Text>
          </Stack>
          <Group gap="xs" mt="md" wrap="wrap">
            <Button
              size="xs"
              variant="filled"
              loading={syncing === 'regen_openai'}
              disabled={!canEdit}
              onClick={() => void regenerateAssistantAfterUpdates()}
            >
              Перегенерировать после обновлений
            </Button>
            <Button
              size="xs"
              loading={syncing === 'vs'}
              disabled={!canEdit}
              onClick={() => void runSync('vs', () => api.syncVectorStore(projectId))}
            >
              Добавить базу знаний
            </Button>
            <Button
              size="xs"
              loading={syncing === 'asst'}
              disabled={!canEdit}
              onClick={() => void runSync('asst', () => api.syncAssistant(projectId))}
            >
              Добавить ассистента
            </Button>
            <Button
              size="xs"
              color="red"
              variant="light"
              disabled={!canEdit || !settings.openai_vector_store_id}
              loading={syncing === 'dvs'}
              onClick={() => void runSync('dvs', () => api.deleteVectorStore(projectId))}
            >
              Удалить базу
            </Button>
            <Button
              size="xs"
              color="red"
              variant="light"
              disabled={!canEdit || !settings.openai_assistant_id}
              loading={syncing === 'dasst'}
              onClick={() => void runSync('dasst', () => api.deleteAssistant(projectId))}
            >
              Удалить ассистента
            </Button>
          </Group>
        </Card>
      ) : null}

      {provider === 'vllm' ? (
        <Card withBorder padding="md" radius="md">
          <Text fw={600} mb="sm">
            vLLM: подключение, база и ассистент
          </Text>
          <Alert variant="light" color="blue" mb="sm" title="Порядок настройки">
            <Stack gap={4}>
              <Text size="sm">1. Заполните URL, API-ключ и модель → «Сохранить настройки».</Text>
              <Text size="sm">
                2. «Синхронизировать базу» — KB ({activeKbCount} записей) и инструкции (
                {activeInstructionCount} сценариев) в Vector Store.
              </Text>
              <Text size="sm">3. «Синхронизировать ассистента» — короткий system prompt на шлюзе.</Text>
              <Text size="sm" c="dimmed">
                Типовые фразы (триггеры) обрабатываются без GPU; сложные диалоги — один запрос к vLLM.
                Очередь <code>ai</code>, следующий тикет — сразу после ответа.
              </Text>
            </Stack>
          </Alert>

          {isEnabled ? (
            <Alert
              variant="light"
              color={vllmPipelineReady ? 'green' : 'yellow'}
              mb="sm"
              title="Готовность Мозгобота"
            >
              <Stack gap={2}>
                <Text size="sm">{vllmCredentialsOk ? '✓' : '○'} URL и API-ключ</Text>
                <Text size="sm">{vllmModelOk ? '✓' : '○'} Модель маршрута (GPU Balancer)</Text>
                <Text size="sm">
                  {settings.vllm_vector_store_id ? '✓' : '○'} Vector Store синхронизирован
                </Text>
                <Text size="sm">
                  {settings.vllm_assistant_id ? '✓' : '○'} Ассистент (короткий prompt)
                </Text>
                <Text size="sm">
                  {autoRespond ? '✓' : '○'} Автоответ включён · {monitorTickets ? '✓' : '○'} Мониторинг
                  тикетов
                </Text>
              </Stack>
            </Alert>
          ) : null}

          <Stack gap={4} mb="sm">
            <Text size="sm">
              Vector Store: <code>{settings.vllm_vector_store_id || '—'}</code>
            </Text>
            <Text size="sm">
              Assistant: <code>{settings.vllm_assistant_id || '—'}</code>
            </Text>
            <Text size="sm" c="dimmed">
              Qdrant: <code>{settings.vllm_qdrant_collection || qdrantCollection || '—'}</code>
            </Text>
            <Text size="sm" c="dimmed">
              Модель в БД: <code>{settings.local_ai_model || '—'}</code>
              {localModel.trim() && localModel !== (settings.local_ai_model || '') ? (
                <> · в форме: <code>{localModel}</code> (сохраните)</>
              ) : null}
            </Text>
          </Stack>

          <Group gap="xs" mt="md" wrap="wrap">
            <Button
              size="xs"
              variant="filled"
              loading={syncing === 'regen_vllm'}
              disabled={!canEdit || !vllmCredentialsOk}
              onClick={() => void regenerateAssistantAfterUpdates()}
            >
              Перегенерировать после обновлений
            </Button>
            <Button
              size="xs"
              loading={syncing === 'vvs'}
              disabled={!canEdit || !vllmCredentialsOk}
              onClick={() => void runSync('vvs', () => api.syncVLLMVectorStore(projectId))}
            >
              1. Синхронизировать базу (KB + сценарии)
            </Button>
            <Button
              size="xs"
              loading={syncing === 'vasst'}
              disabled={!canEdit || !vllmCredentialsOk || !settings.vllm_vector_store_id}
              onClick={() => void runSync('vasst', () => api.syncVLLMAssistant(projectId))}
            >
              2. Синхронизировать ассистента
            </Button>
            <Button
              size="xs"
              color="red"
              variant="light"
              disabled={!canEdit || !settings.vllm_vector_store_id}
              loading={syncing === 'dvvs'}
              onClick={() =>
                void runSync('dvvs', () => api.deleteVLLMVectorStore(projectId, settings.vllm_vector_store_id!))
              }
            >
              Удалить базу
            </Button>
            <Button
              size="xs"
              color="red"
              variant="light"
              disabled={!canEdit || !settings.vllm_assistant_id}
              loading={syncing === 'dvasst'}
              onClick={() =>
                void runSync('dvasst', () => api.deleteVLLMAssistantResource(projectId, settings.vllm_assistant_id!))
              }
            >
              Удалить ассистента
            </Button>
          </Group>
        </Card>
      ) : null}

      <Card withBorder padding="md" radius="md">
        <Text fw={600} mb="sm">
          Резервный канал (fallback)
        </Text>
        <NativeSelect
          label="Резервный провайдер"
          data={[
            { value: '', label: 'Не использовать' },
            { value: 'openai', label: 'OpenAI' },
            { value: 'vllm', label: 'vLLM' },
          ]}
          value={fallbackProvider}
          onChange={(e) => setFallbackProvider(e.currentTarget.value)}
          disabled={!canEdit}
        />
        {fallbackProvider === 'openai' ? (
          <Stack gap="sm" mt="sm">
            <TextInput label="OpenAI API ключ" type="password" value={fallbackOpenaiKey} onChange={(e) => setFallbackOpenaiKey(e.target.value)} disabled={!canEdit} />
            <TextInput label="Assistant ID" value={fallbackAsstId} onChange={(e) => setFallbackAsstId(e.target.value)} disabled={!canEdit} />
          </Stack>
        ) : null}
        {fallbackProvider === 'vllm' ? (
          <Stack gap="sm" mt="sm">
            <TextInput label="vLLM URL" value={fallbackVllmUrl} onChange={(e) => setFallbackVllmUrl(e.target.value)} disabled={!canEdit} />
            <TextInput label="vLLM API ключ" type="password" value={fallbackVllmKey} onChange={(e) => setFallbackVllmKey(e.target.value)} disabled={!canEdit} />
            <TextInput label="Assistant ID" value={fallbackVllmAsst} onChange={(e) => setFallbackVllmAsst(e.target.value)} disabled={!canEdit} />
          </Stack>
        ) : null}
      </Card>

      {canEdit ? (
        <Button loading={saving} onClick={() => void save()} w="fit-content">
          Сохранить настройки
        </Button>
      ) : null}

      <Modal
        opened={disableConfirmOpen}
        onClose={() => {
          setDisableConfirmOpen(false)
          setIsEnabled(true)
        }}
        title="Отключить Мозгобота?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            В {projectScopeLabel} есть обращения, назначенные на Мозгобота. Можно снять
            назначение с открытых, новых и находящихся в работе обращений и вернуть их в
            список «Неназначенные». Решённые и закрытые обращения останутся на
            Мозгоботе в текущем статусе.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => {
                setDisableConfirmOpen(false)
                setIsEnabled(true)
              }}
              disabled={saving}
            >
              Отмена
            </Button>
            <Button
              variant="light"
              onClick={() => void save('keep')}
              loading={saving}
            >
              Только отключить
            </Button>
            <Button
              color="orange"
              onClick={() => void save('release')}
              loading={saving}
            >
              Отключить и снять назначения
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
